import { useCallback, useContext } from 'react';
import { MaterialIcon } from '@pelckmans/business-components/components/material-icon';
import getAccessToken from '../../../../api/BearerToken';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import { useTranslation } from 'react-i18next';
import { getFileTypeDescription } from '../../../../utils/fileType';
import { FileMedialink, getSignedUrl, getSignedUrlForPreview } from '../../_api/MedialinksApi';
import { Node } from '../../_api/TableOfContentsApi';
import MedialinkContentType from '../../_components/common/MedialinkContentType';
import MedialinkInfo from '../../_components/common/MedialinkInfo';
import ImagePreviewModal from '../../_modals/ImagePreviewModal';
import ModuleContext from '../../_contexts/ModuleContext';
import useRouting from '../../../../hooks/useRouting';
import PdfPreviewModal from '../../_modals/PdfPreviewModal';
import PreviewModal from '../../_modals/PreviewModal';
import JWPlayerModal from '../../_modals/JWPlayerModal';

export default function useFileMedialink() {
  const handleError = useThrowAsyncError();
  const { r } = useRouting();
  const { t } = useTranslation();
  const { selectedModule } = useContext(ModuleContext);

  const getDownloadDisclaimer = useCallback(
    (material: FileMedialink) => {
      const hasOnlyDemoLicenseForTeacher =
        selectedModule?.licenses.hasDemoProductLicenseForTeacher &&
        !(selectedModule.licenses.hasDocumentLicenseForTeacher || selectedModule.licenses.hasDocumentLicenseForStudent);

      if (hasOnlyDemoLicenseForTeacher) {
        return (
          <p className="p-no-margin">
            {t('MODULES.MATERIAL.COMPONENTS.MEDIALINK-MODALS.MEDIA.DEMO_PRODUCT_LICENSE_DISCLAIMER')}
          </p>
        );
      }

      if (!material.file.forceDownload) {
        return (
          <p
            className="p-no-margin"
            dangerouslySetInnerHTML={{
              __html: t('MODULES.MATERIAL.COMPONENTS.MEDIALINK-MODALS.MEDIA.NO_DOWNLOAD_AVAILABLE_DISCLAIMER', {
                url: r('terms-and-conditions'),
              }),
            }}
          />
        );
      }
    },
    [r, selectedModule, t],
  );

  const openFileMedialink = useCallback(
    async (material: FileMedialink, node: Node) => {
      const {
        file: { s3file: file },
      } = material;

      const fileType = getFileTypeDescription(file.mimeType).key;

      const accessToken = await getAccessToken();

      const linkUrl = `${process.env.REACT_APP_P_API_URL}/api/s3-files/${file.id}/link`;

      const urlWithAccessToken = `${linkUrl}?${new URLSearchParams({ access_token: accessToken }).toString()}`;

      const downloadUrl = `${linkUrl}?${new URLSearchParams({
        access_token: accessToken,
        attachment: material.name,
      }).toString()}`;

      const downloadDisclaimer = getDownloadDisclaimer(material);

      try {
        if (file.preview) {
          const { url } = await getSignedUrlForPreview(file.id);

          return (
            <PdfPreviewModal
              id="pdf-preview-modal"
              url={url}
              node={node}
              name={material.name}
              contentType={<MedialinkContentType medialink={material} />}
              info={<MedialinkInfo material={material} nodeDescription={node.description} />}
              downloadDisclaimer={downloadDisclaimer}
              downloadUrl={downloadUrl}
              linkTarget={material.file.pdfLinkTarget}
              tutorialId={material.contentType.faqTutorial}
            />
          );
        }

        switch (fileType) {
          case 'IMAGE':
            return (
              <ImagePreviewModal
                id="image-preview-modal"
                downloadDisclaimer={downloadDisclaimer}
                downloadUrl={downloadUrl}
                image={{ src: urlWithAccessToken, alt: material.name }}
                node={node}
                contentType={<MedialinkContentType medialink={material} />}
                name={material.name}
                info={<MedialinkInfo material={material} nodeDescription={node.description} />}
                tutorialId={material.contentType.faqTutorial}
              />
            );

          case 'PDF':
            const { url: signedUrlPdf } = await getSignedUrl(material.id);

            return (
              <PdfPreviewModal
                id="pdf-preview-modal"
                url={signedUrlPdf}
                node={node}
                name={material.name}
                contentType={<MedialinkContentType medialink={material} />}
                info={<MedialinkInfo material={material} nodeDescription={node.description} />}
                downloadDisclaimer={downloadDisclaimer}
                downloadUrl={downloadUrl}
                linkTarget={material.file.pdfLinkTarget}
                tutorialId={material.contentType.faqTutorial}
              />
            );

          case 'AUDIO':
          case 'VIDEO':
            const { url: signedUrlAudioVideo } = await getSignedUrl(material.id);

            return (
              <JWPlayerModal
                media={material.jwplayer?.ready ? { playlist: signedUrlAudioVideo } : { file: signedUrlAudioVideo }}
                downloadUrl={downloadUrl}
                audioOnly={fileType === 'AUDIO'}
                id={`jwplayer-${fileType.toLocaleLowerCase()}-modal`}
                name={material.name}
                node={node}
                contentType={<MedialinkContentType medialink={material} />}
                downloadDisclaimer={downloadDisclaimer}
                tutorialId={material.contentType.faqTutorial}
              />
            );
        }

        return (
          <PreviewModal
            node={node}
            id="preview-modal"
            name={material.name}
            downloadUrl={downloadUrl}
            downloadDisclaimer={downloadDisclaimer}
            contentType={<MedialinkContentType medialink={material} />}
            icon={<MaterialIcon materialType="medialink" contentType={material.contentType} />}
            tutorialId={material.contentType.faqTutorial}
          >
            <p className="name">{material.name}</p>
            <MedialinkInfo material={material} nodeDescription={node.description} />
          </PreviewModal>
        );
      } catch (error) {
        handleError(error);
      }
    },
    [getDownloadDisclaimer, handleError],
  );

  return { openFileMedialink };
}
